import { useMemo, useCallback, useRef, useState } from 'react'
import { debounce } from '@/utils/debounce'
import type { IndicadorDados } from './types'

/**
 * Fields that should fire immediately (toggles) vs debounced (text/number inputs).
 */
const IMMEDIATE_FIELDS = new Set(['comprovado', 'rhValidado'])

export interface UseIndicatorAutoSaveOptions {
  assessmentId: string | null
  onMutate: (payload: {
    assessmentId: string
    indicadorId: string
    dados: Partial<IndicadorDados>
  }) => void
}

/**
 * Hook that provides a debounced update handler for IndicatorRow.
 *
 * - Text/number fields (valor, evidencia, rhComentario): 500ms debounce
 * - Toggle fields (comprovado, rhValidado): immediate fire
 *
 * Returns { handleUpdate, savingIndicatorIds } for UI feedback.
 */
export function useIndicatorAutoSave({ assessmentId, onMutate }: UseIndicatorAutoSaveOptions) {
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set())
  const debouncedFnsRef = useRef<Map<string, ReturnType<typeof debounce>>>(new Map())

  const fireUpdate = useCallback(
    (indicadorId: string, patch: Partial<IndicadorDados>) => {
      if (!assessmentId) return
      setSavingIds((prev) => new Set(prev).add(indicadorId))
      onMutate({ assessmentId, indicadorId, dados: patch })
      // Clear saving state after a short delay (actual mutation handling is in the caller)
      setTimeout(() => {
        setSavingIds((prev) => {
          const next = new Set(prev)
          next.delete(indicadorId)
          return next
        })
      }, 1000)
    },
    [assessmentId, onMutate]
  )

  const handleUpdate = useCallback(
    (indicadorId: string, patch: Partial<IndicadorDados>) => {
      const fields = Object.keys(patch)
      const isImmediate = fields.some((f) => IMMEDIATE_FIELDS.has(f))

      if (isImmediate) {
        fireUpdate(indicadorId, patch)
        return
      }

      // Debounced update per indicator
      const key = `${indicadorId}-${fields.join(',')}`
      let debouncedFn = debouncedFnsRef.current.get(key)
      if (!debouncedFn) {
        debouncedFn = debounce(
          (id: string, p: Partial<IndicadorDados>) => fireUpdate(id, p),
          500
        )
        debouncedFnsRef.current.set(key, debouncedFn)
      }
      debouncedFn(indicadorId, patch)
    },
    [fireUpdate]
  )

  const isSaving = useCallback(
    (indicadorId: string) => savingIds.has(indicadorId),
    [savingIds]
  )

  // Cleanup on unmount handled by React ref lifecycle

  return { handleUpdate, isSaving }
}
